import { DialogBox } from "@/components/DialogBox/DialogBox";
import { DisplayError } from "@/components/Error/Error";
import { MoreMenuSvg } from "@/components/Icon/Icon";
import { Menu } from "@/components/Menu/Menu";
import { ReduxPerPage } from "@/components/PerPageSelector/PerPageSelector";
import { Profile } from "@/components/Profile/Profile";
import { SearchField } from "@/components/SearchField/SearchField";
import { Column, DynamicTable } from "@/components/Table/Table";
import { USER_TABLE_STATE_NAME } from "@/constants/users";
import { useOrg } from "@/hooks/org";
import { Scope, usePermissions } from "@/hooks/permissions";
import { useRoles } from "@/hooks/role";
import {
  clientApi,
  REFETCH_TIMEOUT,
  useChangeUserRoleApiV1PatchMutation,
  useDeleteUserApiV1UsersUserIdDeleteMutation,
  UserReadDto,
  UsersPaginatedListRead,
  useSearchUsersApiV1UsersSearchGetQuery
} from "@/store/clientApi";
import {
  invalidate,
  selectQueryArgs,
  selectQueryIsSearching,
  selectQuerySearchValue,
  selectRtkData,
  setCanCollapsible,
  setPerPage,
  setRtkArgs,
  setSearchValue,
  setSortField,
  setSortOrder
} from "@/store/slices/tableSlice";
import { RootState, store } from "@/store/store";
import { ensureError, localizeError } from "@/utils/helpers";
import { Box, FormControl, IconButton, InputLabel, LinearProgress, MenuItem, Select, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { createSelector } from "@reduxjs/toolkit";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { shallowEqual, useSelector } from "react-redux";

const invalidateTable = () => store.dispatch(invalidate(USER_TABLE_STATE_NAME));

const ChangeRole = ({
  user,
  changeRoleOpen,
  setChangeRoleOpen
}: {
  user: UserReadDto;
  changeRoleOpen: boolean;
  setChangeRoleOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { t } = useTranslation("users");
  const [role, setRole] = useState<string>("");
  const [roleError, setRoleError] = useState<boolean>(false);
  const { lessPrivilegedRoles, userId: operatingUserId, error: rolesError, isLoading: rolesIsLoading } = useRoles();

  const roles = useMemo(() => {
    const userRole = user?.roles?.[0];
    if (
      operatingUserId !== user.user_id ||
      userRole === undefined ||
      lessPrivilegedRoles.find((role) => role.id === userRole.id) !== undefined
    ) {
      return lessPrivilegedRoles;
    }

    return [...lessPrivilegedRoles, userRole].sort((a, b) => a.name.localeCompare(b.name));
  }, [lessPrivilegedRoles, operatingUserId, user?.roles, user.user_id]);

  useEffect(() => {
    const usersRole = roles.find((role) => role.id === user?.roles?.[0].id);
    if (role === "" && usersRole !== undefined) {
      setRole(usersRole.id);
    }
  }, [user?.roles, roles, role]);

  const [changeUsersRole] = useChangeUserRoleApiV1PatchMutation();

  const changeRole = async () => {
    if (role === "") {
      setRoleError(true);
      return;
    } else {
      setRoleError(false);
    }

    if (user?.roles?.[0]?.id === role) {
      return;
    }

    try {
      if (user?.user_id == undefined) {
        throw new Error(t("common:error.userNotFound"));
      }
      await changeUsersRole({
        userId: user.user_id,
        role
      }).unwrap();

      // sometimes the auth0 database is delayed in updating data
      // so we need to refetch after a delay to ensure data is up to date
      setTimeout(
        () =>
          user?.user_id != undefined &&
          store.dispatch(
            clientApi.util.invalidateTags([
              {
                type: "users",
                id: user.user_id
              }
            ])
          ),
        REFETCH_TIMEOUT
      );
    } catch (err) {
      const error = ensureError(err);
      toast.error(localizeError(t, error));
    }
  };

  return (
    <DialogBox
      args={undefined}
      open={changeRoleOpen}
      setOpen={setChangeRoleOpen}
      title={t("title.changeRole")}
      onClose={(approve) => {
        if (approve) {
          changeRole();
        }
      }}
      confirmText={t("common:button.update")}
      declineText={t("common:button.cancel")}
      message={
        <Box component="div">
          {rolesError ? (
            localizeError(t, ensureError(roleError))
          ) : rolesIsLoading ? (
            <LinearProgress />
          ) : roles && user?.roles ? (
            <>
              <Typography sx={{ marginBottom: "1em" }}>
                {
                  /*
                  t('users:message.changeRole')
                  t('users:message.changeRole_user')
                   */
                  t("users:message.changeRole", {
                    context: "user",
                    ...(user?.name && { user: user?.name })
                  })
                }
              </Typography>
              <FormControl component="form" id="role-form" style={{ marginBottom: "0.7em", width: "100%" }}>
                <InputLabel id="role-label" htmlFor="change-role-select">
                  {t("title.role")}
                </InputLabel>
                <Select
                  fullWidth
                  required
                  labelId="role-label"
                  label={t("title.role")}
                  id="change-role"
                  onChange={(e) => setRole(e.target.value)}
                  value={role}
                  error={roleError}
                  inputProps={{
                    id: "change-role-select"
                  }}
                >
                  {roles.map((role) => {
                    /*
                  role keys
                  t('role.admin')
                  t('role.owner')
                  t('role.user')
                  */
                    return (
                      <MenuItem id={`select-${role.name}`} key={role.id} value={role.id}>
                        {t(`role.${role.name}`, { defaultValue: role.name })}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </>
          ) : null}
        </Box>
      }
      sx={{ ".MuiPaper-root": { width: "100%", margin: "16px" } }}
    />
  );
};

const SearchBar = () => {
  const { t } = useTranslation("users");
  const searchValue = useSelector((state: RootState) => selectQuerySearchValue(state, USER_TABLE_STATE_NAME)) ?? "";

  return (
    <Box
      component="div"
      sx={{
        display: "flex",
        justifyContent: "space-between",
        gap: "0.5em"
      }}
    >
      <Box
        component="div"
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          gap: "0.5em"
        }}
      >
        <SearchField
          id="search-users"
          placeholder={t("users:label.search")}
          value={searchValue}
          onChange={(searchInput) => {
            store.dispatch(
              setSearchValue({
                name: USER_TABLE_STATE_NAME,
                value: searchInput
              })
            );
            invalidateTable();
          }}
        />
      </Box>
      <ReduxPerPage
        selector={(state: RootState) => state.tables.data[USER_TABLE_STATE_NAME]?.queryArgs?.perPage}
        dispatch={(state) => store.dispatch(setPerPage({ name: USER_TABLE_STATE_NAME, value: state }))}
      />
    </Box>
  );
};

const Edit = ({ userId, user }: { userId: string; user: UserReadDto }) => {
  const { t } = useTranslation("users");
  const { [Scope.DeleteMembers]: enableRemoveUserButton, [Scope.UpdateMembers]: enableChangeRoleButton } = usePermissions();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(null);
  };

  const { org } = useOrg();

  const [deleteUser] = useDeleteUserApiV1UsersUserIdDeleteMutation();

  const [changeRoleOpen, setChangeRoleOpen] = useState<boolean>(false);

  const [removeOpen, setRemoveOpen] = useState<boolean>(false);
  const [removeArgs, setRemoveArgs] = useState<{ [id: string]: boolean }>({});
  const removeCount = Object.values(removeArgs).filter((value) => value).length;

  const { canOperateOnUser } = useRoles({ canOperateOnTargetUser: user });

  const refetch = () => {
    store.dispatch(clientApi.util.invalidateTags(["users"]));
    invalidateTable();
  };

  const deleteAccounts = async (approve: boolean) => {
    if (approve) {
      invalidateTable();
      try {
        await deleteUser({ userId: encodeURI(userId) }).unwrap();
      } catch (err) {
        const error = ensureError(err);
        toast.error(localizeError(t, error));
      }
      setTimeout(refetch, REFETCH_TIMEOUT); // sometimes the auth0 database is delayed in updating data so we need to refetch after a delay to ensure data is up to date
    }
  };

  return (
    <>
      <IconButton onClick={handleMenuClick} sx={{ ...(openMenu && { visibility: "visible" }) }}>
        <MoreMenuSvg color="inherit" />
      </IconButton>
      <Menu
        innerProps={{
          anchorEl: anchorEl,
          open: openMenu,
          onClose: handleMenuClose,
          onClick: handleMenuClose
        }}
        hideArrow
      >
        <MenuItem
          onClick={() => {
            setChangeRoleOpen(true);
          }}
          disabled={!enableChangeRoleButton || !canOperateOnUser}
        >
          {t("button.changeRole")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setRemoveArgs({ [userId]: true });
            setRemoveOpen(true);
          }}
          disabled={!enableRemoveUserButton || !canOperateOnUser}
        >
          {t("common:button.remove")}
        </MenuItem>
      </Menu>
      <DialogBox
        args={removeArgs}
        open={removeOpen}
        setOpen={setRemoveOpen}
        title={t("title.remove", {
          count: removeCount
        })}
        message={
          /*
          t('users:message.confirmDelAccounts_one')
          t('users:message.confirmDelAccounts_other')
          t('users:message.confirmDelAccounts_org_one')
          t('users:message.confirmDelAccounts_org_other')
          */
          t("users:message.confirmDelAccounts", {
            count: removeCount,
            ...(org?.name && { context: "org", org: org?.name })
          })
        }
        onClose={deleteAccounts}
        confirmColor="error"
        confirmText={t("common:button.remove")}
        declineText={t("common:button.cancel")}
        sx={{ ".MuiPaper-root": { width: "100%", margin: "16px" } }}
      />
      {canOperateOnUser && <ChangeRole user={user} changeRoleOpen={changeRoleOpen} setChangeRoleOpen={setChangeRoleOpen} />}
    </>
  );
};

const EntityCell = ({ userId, column }: { userId: string; column: Column }) => {
  const { t } = useTranslation("users");

  const NAME = t("title.name");
  const EMAIL = t("title.email");
  const ROLE = t("title.role");
  const EDIT = t("title.edit");
  const UNAVAILABLE = t("common:loading.unavailable");

  useEffect(() => {
    store.dispatch(setCanCollapsible({ name: USER_TABLE_STATE_NAME, index: userId, value: false }));
  }, [userId]);

  const user = useSelector((state: RootState) =>
    selectRtkData(state, USER_TABLE_STATE_NAME, clientApi.endpoints.searchUsersApiV1UsersSearchGet.select)?.data?.content?.find(
      (user) => user?.user_id === userId
    )
  );

  return (
    <Box
      component="div"
      sx={{
        containerType: "inline-size",
        width: "100%",
        cursor: "pointer"
      }}
    >
      {column.name === NAME && <Profile picture={user?.picture ?? UNAVAILABLE} name={user?.name ?? UNAVAILABLE} />}
      {column.name === EMAIL && (
        <Typography sx={{ cursor: "text", userSelect: "text" }} onMouseDown={(e) => e.stopPropagation()} noWrap>
          {user?.email ?? UNAVAILABLE}
        </Typography>
      )}
      {column.name === ROLE && (
        /*
        role keys
        t('role.admin')
        t('role.owner')
        t('role.user')
        */
        <Typography sx={{ cursor: "text", userSelect: "text" }} onMouseDown={(e) => e.stopPropagation()} noWrap>
          {user?.roles && user?.roles.length > 0 ? t(`role.${user?.roles[0].name}`) : UNAVAILABLE}
        </Typography>
      )}
      {column.name === EDIT && user !== undefined && <Edit userId={userId} user={user} />}
    </Box>
  );
};

const UsersListData = () => {
  const queryArgs = useSelector((state: RootState) => selectQueryArgs(state, USER_TABLE_STATE_NAME));
  const rtkArgs = useMemo(() => {
    return {
      page: queryArgs.page !== 0 ? queryArgs.page - 1 : queryArgs.page,
      perPage: queryArgs.perPage,
      query: queryArgs.searchValue !== "" ? `name:/.*${queryArgs.searchValue}*./ OR email:/.*${queryArgs.searchValue}*./` : undefined,
      sorting: queryArgs.sortField !== "" ? queryArgs.sortField + ":" + (queryArgs.sortOrder === "asc" ? "1" : "-1") : undefined
    };
  }, [queryArgs]);

  useEffect(() => {
    store.dispatch(setRtkArgs({ name: USER_TABLE_STATE_NAME, value: rtkArgs }));
  }, [rtkArgs]);

  const { isFetching } = useSearchUsersApiV1UsersSearchGetQuery(rtkArgs, {});

  return <>{isFetching && <div id="loading-users" style={{ display: "hidden" }}></div>}</>;
};

const UserTable = () => {
  const { t } = useTranslation("users");
  const { [Scope.ReadMembers]: allowUserManagementUi } = usePermissions();
  const NAME = t("title.name");
  const EMAIL = t("title.email");
  const ROLE = t("title.role");
  const EDIT = t("title.edit");

  const SortFields = ["email", "name", "role"];

  const theme = useTheme();

  const isSearching = useSelector((state: RootState) => selectQueryIsSearching(state, USER_TABLE_STATE_NAME));
  const selectUserIds = useMemo(() => {
    return createSelector(
      [(res?: UsersPaginatedListRead) => res?.content],
      (content) => content?.map((user) => user?.user_id)?.filter((userId) => userId != null) as string[] | undefined,
      {
        memoizeOptions: {
          resultEqualityCheck: shallowEqual
        }
      }
    );
  }, []);

  const data = useSelector((state: RootState) =>
    selectUserIds(selectRtkData(state, USER_TABLE_STATE_NAME, clientApi.endpoints.searchUsersApiV1UsersSearchGet.select)?.data)
  );
  const totalCount = useSelector((state: RootState) => {
    const query = selectRtkData(state, USER_TABLE_STATE_NAME, clientApi.endpoints.searchUsersApiV1UsersSearchGet.select);
    return query?.data?.total_count ?? query?.data?.content?.length;
  });
  const error = useSelector(
    (state: RootState) => selectRtkData(state, USER_TABLE_STATE_NAME, clientApi.endpoints.searchUsersApiV1UsersSearchGet.select)?.error
  );
  const isLoading = useSelector((state: RootState) => {
    const query = selectRtkData(state, USER_TABLE_STATE_NAME, clientApi.endpoints.searchUsersApiV1UsersSearchGet.select);

    return query?.isLoading && query?.isUninitialized;
  });

  const { error: totalError, isLoading: totalIsLoading } = useSearchUsersApiV1UsersSearchGetQuery({}, { skip: !allowUserManagementUi });

  const columns: Column[] = [
    { name: NAME, sortable: true, key: SortFields[1], minWidth: 200 },
    { name: EMAIL, sortable: true, key: SortFields[0], minWidth: 200 },
    { name: ROLE, sortable: false },
    { name: EDIT, sortable: false, hover: true }
  ];
  const checkable = useMediaQuery(theme.breakpoints.up("sm"));

  if ((error || totalError) && data === undefined) {
    // Only show error if there is no data to show
    return <DisplayError error={error} />;
  }

  if (isLoading || totalIsLoading || allowUserManagementUi === undefined) {
    return <LinearProgress />;
  }

  return (
    <DynamicTable
      stateName={USER_TABLE_STATE_NAME}
      checkable={checkable}
      columns={columns}
      tableData={data ?? []}
      totalNumEntities={totalCount ?? 0}
      onSort={(sort) => {
        if (sort !== undefined && sort.key !== undefined) {
          store.dispatch(
            setSortField({
              name: USER_TABLE_STATE_NAME,
              value: sort.key
            })
          );
          store.dispatch(
            setSortOrder({
              name: USER_TABLE_STATE_NAME,
              value: sort.order
            })
          );
        } else {
          store.dispatch(
            setSortField({
              name: USER_TABLE_STATE_NAME,
              value: SortFields[1]
            })
          );
          store.dispatch(
            setSortOrder({
              name: USER_TABLE_STATE_NAME,
              value: "asc"
            })
          );
        }
      }}
      noMatch={isSearching && totalCount === 0}
      renderEntry={(user: string) => columns.map((column, index) => <EntityCell key={index} column={column} userId={user} />)}
      menuOptions={[]}
      sx={{
        svg: {
          color: "primary.main"
        }
      }}
      mapId={(user) => user}
    />
  );
};

const UsersList = () => {
  return (
    <>
      <UsersListData />
      <Box
        component="div"
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100%",
          pt: "0.2em"
        }}
      >
        <SearchBar />
        <UserTable />
      </Box>
    </>
  );
};

export { UsersList };
